// @flow
import React, {useEffect, useState} from "react";
import {Modal, Radio, Space, Typography, Alert, Select} from "antd";
import type {proofModalPropTypes} from "./proofModalPropTypes";
import {formatMomentDate} from "../../../../../utils/helpers";
import GoogleMapReact from "google-map-react";
import apiKey from "../../../../../_core/apiKey";
import {Marker, MapContainer} from "../orderDetailStyles";
import {EnvironmentOutlined, UserOutlined, EyeOutlined} from "@ant-design/icons";
import {courierStatusColors} from "../../../../../constants/constants";
import {ProofImage, InfoSpace, LockContainer, LockIcon} from "./proofModalStyles";
import {useTranslation} from "react-i18next";

export const ProofModal = ({
    visible,
    data,
    handleClose,
    changeDeliveryState
}: proofModalPropTypes) => {
    const [deliveryState, setDeliveryState] = useState("");
    const {t} = useTranslation(["proofModal"]);
    const [defaultZoom, setDefaultZoom] = useState(16);
    const {step, proof} = data;
    const [stateTrigger, setStateTrigger] = useState();
    const [label, setLabel] = useState("");
    const [localProof, setLocalProof] = useState();
    const [selectedOrderNumber, setSelectedOrderNumber] = useState(proof?.orderNumber);
    const [labelOptions, setLabelOptions] = useState([]);
    const deliveryOptions = [
        {
            label: t("proofModal.deliveryOptions.onPickUpAddress", {ns: "proofModal"}),
            value: "onPickUpAddress"
        },
        {
            label: t("proofModal.deliveryOptions.onDeliveryAddress", {ns: "proofModal"}),
            value: "onDeliveryAddress"
        }
    ];

    const photoMarker = () => {
        return {
            icon: <UserOutlined />,
            color: "#6c757d",
            text: t("proofModal.photo", {ns: "proofModal"})
        };
    };
    const locationMarker = state => {
        return {
            icon: <EnvironmentOutlined />,
            color: courierStatusColors.onPickUpAddress,
            text:
                state === "onPickUpAddress"
                    ? t("fields.pickupPoint", {ns: "common"})
                    : t("fields.customer", {ns: "common"})
        };
    };
    const {Text} = Typography;

    useEffect(() => {
        if (stateTrigger) {
            const orderNumber =
                localProof?.orderNumber || step?.stepInfo?.orderNumber || selectedOrderNumber;
            if (step.proofs) {
                const clone = [...step.proofs];
                const labelOpt = clone
                    .filter(proof => proof.orderNumber === orderNumber)
                    .map(proof => ({label: proof.label, value: proof._id}));

                setLabelOptions(labelOpt);

                const tempLocalProof = step.proofs.find(proof => proof.orderNumber === orderNumber);
                setLocalProof(tempLocalProof);
                setLabel(tempLocalProof?._id);
            }
        }
    }, [stateTrigger]);

    useEffect(() => {
        const clone = [...step.proofs];
        const labelOpt = clone
            .filter(proof => proof.orderNumber === selectedOrderNumber)
            .map(proof => ({label: proof.label, value: proof._id}));
        setLabelOptions(labelOpt);
        const tempLocalProof = step.proofs.find(proof => proof._id === labelOpt[0]?.value);
        setLocalProof(tempLocalProof);
        setLabel(tempLocalProof?._id);
        setStateTrigger(new Date());
    }, [selectedOrderNumber]);

    useEffect(() => {
        setDeliveryState(proof?.deliveryState);
    }, []);

    const renderInfoTab = () => {
        const orderNumbers = step?.proofs?.map(proof => proof.orderNumber);
        return (
            <>
                {proof && localProof ? (
                    <>
                        <Space>
                            <Text strong>{`${t("fields.orderNo", {ns: "common"})}:`}</Text>
                            <Select
                                showSearch
                                value={selectedOrderNumber}
                                size="small"
                                onChange={value => {
                                    setSelectedOrderNumber(value);
                                    changeDeliveryState(value, deliveryState);
                                }}
                            >
                                {Array.from(new Set(orderNumbers))?.map(orderNumber => (
                                    <>
                                        <Select.Option value={orderNumber}>
                                            {orderNumber}
                                        </Select.Option>
                                    </>
                                ))}
                            </Select>
                        </Space>

                        {labelOptions.length > 0 &&
                        labelOptions?.[0]?.label &&
                        labelOptions?.[0]?.label !== "photo" ? (
                            <Radio.Group
                                options={labelOptions}
                                onChange={e => {
                                    setLabel(e.target.value);
                                    const selectedProof = step.proofs.find(
                                        proof => proof._id === e.target.value
                                    );
                                    setLocalProof(selectedProof);
                                }}
                                value={label}
                            />
                        ) : null}
                        <Space>
                            <Text strong>{`${t("fields.customer", {ns: "common"})}: `}</Text>
                            <Text>{step?.receiverName}</Text>
                        </Space>

                        <Space>
                            <Text strong>{`${t("fields.date", {ns: "common"})}: `}</Text>
                            <Text>{formatMomentDate(localProof?.uploadDate)}</Text>
                        </Space>

                        {localProof?.isHidden ? (
                            <LockContainer>
                                <LockIcon />
                            </LockContainer>
                        ) : (
                            <ProofImage
                                preview={{
                                    mask: (
                                        <div>
                                            <EyeOutlined />{" "}
                                            {t("proofModal.actions.viewFullscreen", {
                                                ns: "proofModal"
                                            })}
                                        </div>
                                    )
                                }}
                                src={`data:image/jpeg;base64,${localProof?.photo}`}
                            />
                        )}
                    </>
                ) : null}
            </>
        );
    };

    const renderMap = () => {
        const {stepInfo} = step;
        if (step?.proof?.required) {
            if (proof) {
                const {coordinates: photoLocation} = proof?.location;
                const customerLocation =
                    proof?.deliveryState === "onPickUpAddress"
                        ? [stepInfo?.location.longitude, stepInfo?.location.latitude]
                        : [stepInfo?.receiverLocation.lon, stepInfo?.receiverLocation.lat];

                return (
                    <MapContainer style={{height: "200px"}}>
                        <GoogleMapReact
                            bootstrapURLKeys={{key: apiKey, language: "tr"}}
                            center={{lat: customerLocation[1], lng: customerLocation[0]}}
                            defaultZoom={defaultZoom}
                            options={{
                                fullscreenControl: false,
                                gestureHandling: "greedy"
                            }}
                            heatmapLibrary={true}
                            yesIWantToUseGoogleMapApiInternals
                        >
                            {renderMarkers([
                                {...photoMarker(), location: photoLocation},
                                {
                                    ...locationMarker(proof?.deliveryState),
                                    location: customerLocation
                                }
                            ])}
                        </GoogleMapReact>
                    </MapContainer>
                );
            } else {
                return (
                    <Alert
                        message={t("proofModal.warning", {ns: "proofModal"})}
                        description={t("proofModal.notDeliveredYet", {
                            ns: "proofModal",
                            orderNumber: selectedOrderNumber
                        })}
                        type="warning"
                        showIcon
                        style={{marginTop: "10px"}}
                    />
                );
            }
        } else {
            return (
                <Alert
                    message={t("proofModal.warning", {ns: "proofModal"})}
                    description={t("proofModal.notRequiredThisStep", {
                        ns: "proofModal",
                        orderNumber: selectedOrderNumber
                    })}
                    type="warning"
                    showIcon
                    style={{marginTop: "10px"}}
                />
            );
        }
    };

    const renderMarkers = markers => {
        return markers.map(marker => {
            return (
                <Marker
                    key={marker?.location[0]}
                    lat={marker?.location[1]}
                    lng={marker?.location[0]}
                    size={20}
                    icon={marker?.icon}
                    color={marker?.color}
                    text={marker?.text}
                />
            );
        });
    };

    return (
        <Modal visible={visible} data={data} onCancel={handleClose} footer={null}>
            <InfoSpace direction={"vertical"}>
                <Radio.Group
                    options={deliveryOptions}
                    onChange={e => {
                        const orderNumber = localProof?.orderNumber || step?.stepInfo?.orderNumber;
                        changeDeliveryState(orderNumber || data.orderNumber, e.target.value);
                        setDeliveryState(e.target.value);
                        setStateTrigger(new Date());
                    }}
                    value={deliveryState}
                    optionType="button"
                />
                {renderInfoTab()}
            </InfoSpace>
            {renderMap()}
        </Modal>
    );
};
